import type { ReactNode } from "react"
import { createPortal } from "react-dom"
import cx from "clsx"
import type { SortableItem } from "./useSortableItem"
import styles from "./sortable.module.css"

/**
 * The copy of a sortable item that follows the pointer while it is dragged, in
 * place of the browser's own picture of it. Give it the item's own class and
 * contents, so the copy is the item itself, lifted; it is inert, and hidden
 * from assistive technology, as only a picture.
 */
export function DragPreview({
  item,
  className,
  children,
}: {
  item: SortableItem
  className?: string
  children: ReactNode
}) {
  if (!item.dragging) return null
  return createPortal(
    <div
      ref={item.previewRef}
      className={cx(className, styles.preview)}
      style={PLACE}
      data-testid="drag-preview"
      data-removing={item.removing || undefined}
      aria-hidden="true"
      inert
    >
      {children}
    </div>,
    document.body,
  )
}

// Over the page, from its corner, where useSortableItem's transform puts it
// under the pointer; set here rather than in a class, so the item's own class
// can't override it, and the pointer passes through to what is under it.
const PLACE = {
  position: "fixed",
  top: 0,
  left: 0,
  zIndex: 1000,
  pointerEvents: "none",
} as const
